import { useState, useEffect } from 'react';

export const useNflCalendar = (selectedSport) => {
  const [calendarData, setCalendarData] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCalendar = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/games/nfl/schedule/calendar');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Filter to show regular season weeks (1-18) and playoff weeks (seasonTypeValue: 3)
        console.log('NFL Calendar raw data:', data.weeks?.slice(0, 5)); // Debug first 5 weeks

        // Map playoff week numbers to friendly labels
        const playoffWeekLabels = {
          '1': 'Wild Card',
          '2': 'Divisional',
          '3': 'Conference',
          '4': 'Pro Bowl',
          '5': 'Super Bowl'
        };

        const filteredWeeks = data.weeks
          .filter(week => {
            const weekNum = parseInt(week.value);
            const seasonType = week.seasonType?.toLowerCase() || '';
            const seasonTypeValue = parseInt(week.seasonTypeValue);

            // Show Regular Season weeks (1-18) OR Playoff weeks (seasonTypeValue: 3)
            const isRegularSeason = seasonType === 'regular season' && weekNum >= 1 && weekNum <= 18;
            const isPlayoffs = seasonTypeValue === 3;
            return isRegularSeason || isPlayoffs;
          })
          .map(week => {
            const seasonTypeValue = parseInt(week.seasonTypeValue);
            const isPlayoffs = seasonTypeValue === 3;

            if (isPlayoffs) {
              // Transform playoff weeks to have unique values and friendly labels
              return {
                ...week,
                value: `playoff-${week.value}`, // Unique identifier like "playoff-1"
                label: playoffWeekLabels[week.value] || `Playoff Week ${week.value}`,
                originalValue: week.value, // Keep original for API calls
                isPlayoffs: true
              };
            }
            return { ...week, isPlayoffs: false };
          });

        // Deduplicate weeks by value (now unique due to playoff- prefix)
        const deduplicatedWeeks = filteredWeeks.filter((week, index, array) =>
          array.findIndex(w => w.value === week.value) === index
        );
        
        const filteredData = {
          ...data,
          weeks: deduplicatedWeeks
        };
        setCalendarData(filteredData);
        setCurrentWeek(data.currentWeek);
        
        // Find the current week based on current EST date + 1 day offset
        // This shifts selection to the upcoming week (e.g., if today is Monday after games, show next week)
        const now = new Date();
        now.setDate(now.getDate() + 1); // Add 1 day offset
        console.log('Current date for NFL week calculation (with +1 day offset):', now.toISOString());
        console.log('Available weeks:', deduplicatedWeeks.map(w => ({
          value: w.value,
          label: w.label,
          startDate: w.startDate
        })));
        
        // Find which week period we're currently in based on startDate
        let weekToSelect = null;
        for (let i = 0; i < deduplicatedWeeks.length; i++) {
          const week = deduplicatedWeeks[i];
          const weekStartDate = new Date(week.startDate);
          const nextWeekStartDate = i < deduplicatedWeeks.length - 1
            ? new Date(deduplicatedWeeks[i + 1].startDate)
            : null;

          console.log(`Checking week ${week.value} (${week.label}):`, {
            weekStartDate: weekStartDate.toISOString(),
            nextWeekStartDate: nextWeekStartDate?.toISOString(),
            nowGreaterThanStart: now >= weekStartDate,
            nowLessThanNext: !nextWeekStartDate || now < nextWeekStartDate
          });

          // Check if current date is >= this week's start and < next week's start
          if (now >= weekStartDate && (!nextWeekStartDate || now < nextWeekStartDate)) {
            // Use the value directly - can be number (regular) or string (playoff-X)
            weekToSelect = week.isPlayoffs ? week.value : parseInt(week.value);
            console.log(`Selected week: ${weekToSelect} (${week.label})`);
            break;
          }
        }

        console.log('Final week selection:', weekToSelect, 'fallback currentWeek:', data.currentWeek);

        // Prefer the week computed from today's date. If no range matched
        // (preseason gap before Reg-Season Week 1, or post-Super Bowl), snap
        // to the nearest end of the visible window rather than trusting
        // `data.currentWeek` — that value can be a preseason week number that
        // collides with regular-season numbering (e.g. Preseason Wk 4 would
        // otherwise default the dropdown to Regular-Season Wk 4).
        let fallback = null;
        if (deduplicatedWeeks.length > 0) {
          const first = deduplicatedWeeks[0];
          const last = deduplicatedWeeks[deduplicatedWeeks.length - 1];
          if (now < new Date(first.startDate)) {
            fallback = first.isPlayoffs ? first.value : parseInt(first.value);
          } else {
            fallback = last.isPlayoffs ? last.value : parseInt(last.value);
          }
        }
        setSelectedWeek(weekToSelect ?? fallback);
      } else {
        throw new Error('Failed to fetch calendar data');
      }
    } catch (err) {
      console.error('Error fetching NFL calendar:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSport === 'nfl') {
      fetchCalendar();
    } else {
      // Reset state when switching away from NFL
      setCalendarData(null);
      setCurrentWeek(null);
      setSelectedWeek(null);
      setError(null);
    }
  }, [selectedSport]);

  const getCurrentWeekData = () => {
    if (!calendarData || !currentWeek) return null;
    return calendarData.weeks.find(week => week.value === String(currentWeek));
  };

  const getSelectedWeekData = () => {
    if (!calendarData || !selectedWeek) return null;
    return calendarData.weeks.find(week => week.value === String(selectedWeek));
  };

  return {
    calendarData,
    currentWeek,
    selectedWeek,
    setSelectedWeek,
    getCurrentWeekData,
    getSelectedWeekData,
    isLoading,
    error,
    refetch: fetchCalendar
  };
};